import { Config } from 'final-form';
import React, { useContext, useEffect } from 'react';
import { Form, FormProps, FormRenderProps } from 'react-final-form';
import { useLocation } from 'react-router-dom';
import { ProgressStore, RoutesContext, RoutesStore } from '../../../context';
import { history } from '../../../history';

export interface MainFormProps<
  FormValues = Record<string, any>,
  InitialFormValues = Partial<FormValues>,
> extends Config<FormValues, InitialFormValues> {
  children:
    | React.ReactNode
    | React.FunctionComponent<FormRenderProps<FormValues, InitialFormValues> & {
      route?: Pick<RoutesContext, 'currentRoute' | 'nextRoute' | 'previousRoute'>;
    }>;
  /**
   * The action to perform when the 'submit' button is clicked,
   * the default is 'nextRoute' which loads 'useContext(RoutesStore).nextRoute.path',
   * null defaults to 'nextRoute', 'nothing' does nothing after the button is clicked
   */
  submitAction?: 'nextRoute' | 'nothing' | null;
}

/**
 * A react-final-form <Form> with some automated form props handling.
 * @param props the form props with a 'children' node or component
 * @returns the new form element
 */
export function MainForm<T = Record<string, any>, P = Partial<T>>(
  props: MainFormProps<T, P>,
): React.ReactElement<any, any> {
  const { children, ...formProps } = props;
  const { pathname } = useLocation();
  const { dispatch: progressDispatch } = useContext(ProgressStore);
  const { currentRoute, nextRoute, previousRoute } = useContext(RoutesStore);
  const nextPath = nextRoute?.path;

  formProps.mutators = formProps.mutators || {
    setFieldValue: ([name, value], state, utils) => {
      utils.changeValue(state, name, _ => value);
    },
  };
  (formProps as any).route = {
    currentRoute,
    nextRoute,
    previousRoute,
  };

  useEffect(() => {
    progressDispatch({ type: 'SET_CURRENT_STEP', payload: { pathname } });
  }, [pathname, progressDispatch]);

  return (
    <Form<T, P> {...(formProps as FormProps<T, P>)}>
      {subProps => (
        <form onSubmit={event => {
          if (subProps.handleSubmit) {
            const res = subProps.handleSubmit(event);
            const submitTask = isPromise(res)
              ? res
              : new Promise((resolve, reject) => {
                if ((res as any) === true || res == null) {
                  resolve(res);
                } else {
                  reject(res);
                }
              });

            return submitTask.then((result) => {
              progressDispatch({
                type: 'SET_STEP_VALIDATION_STATE',
                payload: {
                  id: currentRoute?.id as any,
                  validationState: result === true || result == null ? 'valid' : 'invalid',
                },
              });
              if (formProps.submitAction !== 'nothing') {
                history.push(nextPath!);
              }
            }).catch((error) => {
              progressDispatch({
                type: 'SET_STEP_VALIDATION_STATE',
                payload: {
                  id: currentRoute?.id as any,
                  validationState: 'invalid',
                },
              });
            });
          }
        }}>
          {typeof children === 'function' ? children(subProps) : children}
        </form>
      )}
    </Form>
  );
}

function isPromise<T = unknown>(obj: Promise<T> | any): obj is Promise<T> {
  return typeof obj?.then === 'function';
}
